/* eslint-disable */
import { useEffect, useMemo, useContext, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { ClientContext } from 'react-fetching-library';
import isEqual from 'lodash/isEqual';
import uniq from 'lodash/uniq';
import { UserContext, AppRouteContext } from 'contexts';
import showServerError from 'helpers/showError';
import HipaaCheck from 'components/Shared/HipaaCheck';
import { useSelector, useDispatch } from 'react-redux';
import {
  enqueueSnackbar,
  SNACKBAR_VARIANTS,
} from '../redux_store/reducer/reducers/notificationsReducer';
import {
  logout as logoutAction,
  getMetadata, getUser,
} from '../redux_store/reducer/reducers/userReducer/api';
import {
  setUser as setUserAction,
  setHipaaCheckParams as setHipaaCheckParamsAction,
  setMetadata as setMetadataAction,
  setBlockedAccounts, setInitPath, getIsLogin, setIsLoginStatus,
} from '../redux_store/reducer/reducers/userReducer/userReducer';
import { getTransitionWithVariable } from '../redux_store/reducer/reducers/i18nReducer';
import { matchPath } from 'react-router-dom';
import { ALL_PRIVATE_ROUTES, ALL_PUBLIC_ROUTES, ALL_ROUTES } from '../config/routes';
import {
  checkAuthorizationExpiresTimeout,
} from '../redux_store/reducer/reducers/appSettingsReducer';
import { REQUEST_STATUS } from '../redux_store/constants';

export const tryToParseRouteFromString = (path, routes) => {
  let parsedRoute;
  (routes || ALL_PRIVATE_ROUTES).forEach(i => {
    const match = matchPath({ path: i.path, end: true }, path);
    if (match) {
      parsedRoute = {
        path: i.path,
        params: {
          ...match.params,
        },
        title: i.additional.title,
      };
    }
  });
  return parsedRoute;
};

let metadata_store = null;


const UserProvider = ({ children }) => {
  const {
    user,
    hipaaCheckParams,
    metadata,
    blockedAccounts,
    isLogin,
    status,
    initPath,
  } = useSelector(
    (state) => state.user,
  );
  const dispatch = useDispatch();
  const clientContext = useContext(ClientContext);
  const { setAppRoute } = useContext(AppRouteContext);
  const { en: { notifications, errors } } = useSelector((state) => state.i18n);
  const userUuid = user ? user.uuid : null;
  const [changeAccountLoading, setChangeAccountLoading] = useState(true);
  const OLD_PATH_FOR_SUPPORT_REDIRECT = {
    '/dashboard': ALL_ROUTES.dashboard.root,
    '/accounts/list': ALL_ROUTES.account.listDS,
    '/patients/list': ALL_ROUTES.patient.list,
    '/orders/list': ALL_ROUTES.order.list,
    '/assessments/list': ALL_ROUTES.assessment.list,
    '/resources/list': ALL_ROUTES.resource.list,
    '/claimsReporting': ALL_ROUTES.claim.list,
    '/claimReport/list': ALL_ROUTES.claimReport.list,
    '/reportables/list': ALL_ROUTES.reportable.list,
  };

  const setMetadata = (newMetadata) => {
    if (!isEqual(metadata, newMetadata)) {
      dispatch(setMetadataAction(newMetadata));
    }
  };

  useEffect(() => {
    metadata_store = metadata;
  }, [metadata]);

  // fetch metadata if user exist
  useEffect(() => {
    if (userUuid) {
      refreshMetadata();
    }
  }, [userUuid]);

  const refreshMetadata = () => {
    dispatch(getMetadata());
  };

  const checkHipaa = (newUser, newMetadata, newRoute) => {
    dispatch(
      setHipaaCheckParamsAction({
        newUser: newUser,
        newMetadata: newMetadata,
        newRoute: newRoute,
      }),
    );
  };

  const changeAccount = async (accountUuid, route) => {
    setChangeAccountLoading(true);
    const { payload, error } = await clientContext.query({
      method: 'POST',
      endpoint: `/auth/token/account/${accountUuid}`,
    });
    if (!error && payload) {
      const { user: newUser, authMetadata } = payload;
      if (!authMetadata) {
        showServerError(
          dispatch,
          errors.doNotHaveAccessToAnyAccounts,
        );
        return;
      }
      checkHipaa(newUser, authMetadata, route);
    } else {
      dispatch(
        enqueueSnackbar({
          message: notifications.noPermissions,
          options: {
            variant: SNACKBAR_VARIANTS.warning,
          },
        }),
      );
      if (metadata?.currentAccount?.uuid) {
        setAppRoute({
          clearBreadcrumbs: true,
          ...ALL_ROUTES.root,
        }, { currentAccountUuid: metadata?.currentAccount?.uuid }, metadata).then(() => {
          setChangeAccountLoading(false);
        });
      } else {
        setAppRoute({
          path: ALL_ROUTES.login.path,
        }).then(() => {
          setChangeAccountLoading(false);
        });
      }
    }
  };

  const handleSuccessHipaaCheck = () => {
    const { newUser, newMetadata, newRoute } = hipaaCheckParams;
    dispatch(setUserAction(newUser));
    if (newMetadata === null || Object.keys(newMetadata).length === 0) {
      showServerError(
        dispatch,
        errors.doNotHaveAccessToAnyAccounts,
      );
    }
    if (newRoute) {
      dispatch(setInitPath(newRoute));
    }
    setMetadata({ ...newMetadata, route: newRoute });
    dispatch(setHipaaCheckParamsAction(null));
    dispatch(setIsLoginStatus(REQUEST_STATUS.succeeded));
  };

  const handleFailHipaaCheck = () => {
    dispatch(logoutAction());
  };

  const setUser = (newUser) => {
    dispatch(setUserAction(newUser));
  };

  const handleSwitchHipaaAccount = useCallback(() => {
    const {
      newMetadata: { accounts, currentAccount },
    } = hipaaCheckParams;
    dispatch(setHipaaCheckParamsAction(null));

    const newBlockedAccounts = uniq([...blockedAccounts, currentAccount.uuid]);
    dispatch(setBlockedAccounts(newBlockedAccounts));
    const accountsToCheck = accounts.filter(
      (item) => !newBlockedAccounts.includes(item.uuid),
    );

    if (accountsToCheck.length) {
      changeAccount(accountsToCheck[0].uuid);
      dispatch(
        enqueueSnackbar({
          message: getTransitionWithVariable(notifications.youHaveBeenSwitched, {
            name: accountsToCheck[0].businessName,
          }),
          options: {
            variant: SNACKBAR_VARIANTS.success,
          },
        }),
      );
      dispatch(setInitPath(null));
    } else {
      dispatch(logoutAction());
    }
  }, [blockedAccounts, hipaaCheckParams]);

  useEffect(() => {
    if (metadata?.currentAccount?.uuid) {
      if (initPath && initPath.params?.currentAccountUuid
        && initPath.params?.currentAccountUuid !== metadata?.currentAccount?.uuid) {
        setChangeAccountLoading(true);
        changeAccount(initPath.params.currentAccountUuid, initPath);
      } else {
        const { pathname } = location;
        let newRoute = initPath;
        if (!initPath || initPath.isUnknown) {
          if (initPath) {
            const parsedRoute = tryToParseRouteFromString(initPath?.path, ALL_PUBLIC_ROUTES);
            const parsedOldRoute = OLD_PATH_FOR_SUPPORT_REDIRECT[initPath?.path];

            if (parsedOldRoute) {
              newRoute = {
                path: parsedOldRoute.path,
                title: parsedOldRoute.additional?.title,
                params: {
                  currentAccountUuid: metadata.currentAccount.uuid,
                },
                clearBreadcrumbs: true,
                removeQueryString: true,
              };
            } else if (parsedRoute) {
              newRoute = ALL_ROUTES.root;
            } else {
              newRoute = {
                path: initPath?.path || pathname,
              };
            }
          } else {
            newRoute = ALL_ROUTES.root;
          }
        }
        setAppRoute({
          clearBreadcrumbs: true,
          ...newRoute,
        }, { currentAccountUuid: metadata?.currentAccount?.uuid }, metadata).then(() => {
          setChangeAccountLoading(false);
        });
        dispatch(setInitPath(null));
      }
    }
  }, [metadata?.currentAccount?.uuid]);

  useEffect(() => {
    dispatch(getIsLogin());
    dispatch(checkAuthorizationExpiresTimeout());

    const { pathname } = location;
    const parsedRoute = tryToParseRouteFromString(pathname);

    if (parsedRoute) {
      dispatch(setInitPath(parsedRoute));
    } else {
      const parsedRoute = tryToParseRouteFromString(pathname, ALL_PUBLIC_ROUTES);
      if (!parsedRoute) {
        dispatch(setInitPath({
          path: pathname,
          isUnknown: true,
        }));
      }
      setChangeAccountLoading(false);
    }

    if (isLogin) {
      dispatch(getUser());
    }
  }, []);

  useEffect(() => {
    if (isLogin && !user) {
      dispatch(getUser());
    }
    if (isLogin === false) {
      const { pathname } = location;
      const parsedRoute = tryToParseRouteFromString(pathname);
      if (!!parsedRoute) {
        setAppRoute({
          path: ALL_ROUTES.login.path,
        }).then(() => {
          setChangeAccountLoading(false);
        });
      }
    }
  }, [isLogin]);

  useEffect(() => {
    if (status === REQUEST_STATUS.failed) {
      setAppRoute({
        path: ALL_ROUTES.login.path,
      }).then(() => {
        setChangeAccountLoading(false);
      });
    }
  }, [status]);


  const eventListener = () => {
    /*
         handle changing route outside the dashboard
         case: browser back button click
       */
    let newRoute = tryToParseRouteFromString(document.location.pathname);
    const accountUuid = metadata_store && metadata_store.currentAccount ? metadata_store.currentAccount.uuid : null;

    if (newRoute?.params?.currentAccountUuid && (newRoute.params.currentAccountUuid !== accountUuid)) {
      setChangeAccountLoading(true);
      changeAccount(newRoute.params.currentAccountUuid, newRoute);
    }
  };

  useEffect(() => {
    window.addEventListener('popstate', eventListener, true);
    return () => {
      window.removeEventListener('popstate', eventListener);
    };
  }, []);

  // memoize a provider value
  const value = useMemo(
    () => ({
      changeAccount,
      checkHipaa,
      handleFailHipaaCheck,
      setUser,
      changeAccountLoading,
    }),
    [user, metadata, changeAccount, changeAccountLoading],
  );

  return (
    <UserContext.Provider value={value}>
      {children}
      {hipaaCheckParams && (
        <HipaaCheck
          user={hipaaCheckParams.newUser}
          metadata={hipaaCheckParams.newMetadata}
          onSuccess={handleSuccessHipaaCheck}
          onFail={handleFailHipaaCheck}
          onSwitchAccount={handleSwitchHipaaAccount}
        />
      )}
    </UserContext.Provider>
  );
};

UserProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
};

export default UserProvider;
